import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Tabs,
  Autocomplete,
  Tab,
  Grid,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Schedule as FollowUpIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon,
  FileUpload as ImportIcon,
  FileDownload as ExportIcon,
  Business as BusinessIcon,
  EventAvailable as VisitIcon,
  WhatsApp as WhatsAppIcon,
  CameraAlt as CameraIcon,
  Star as StarIcon,
  Close,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import AlertModal from '../../components/AlertModal';
import NoRecordFound from '../../components/NoRecordFound';
import ConfirmModal from '../../components/ConfirmModal';
import { useHistory } from 'react-router-dom';
import {
  readExcelFile,
  exportAsExcel,
  exportAsCSV,
  exportAsPDF,
  downloadExcelFile,
} from '../../utils/importExport';
import { siteVisitService } from '../../services/siteVisit';
import { getSources, getInquiryStatuses } from '../../services/master';
import { getAssociates } from '../../services/associate';
import SaveIcon from '@mui/icons-material/Save';
import Loader from '../../themes/useLoader';
import { hasReadPermission, hasWritePermission } from '../../utils/menuFilter';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import MoreFilters from '../../components/MoreFilters/MoreFilters';

const mapSiteVisit = (v) => ({
  id: v._id,
  visitId: `VT${v.siteVisitId ?? v.visitNo ?? ''}`,
  visitNo: v.siteVisitId ?? v.visitNo,
  leadId: v.leadId ? (typeof v.leadId === 'object' ? `LD${v.leadId.leadNo ?? ''}` : `LD${v.leadNo ?? ''}`) : `LD${v.leadNo ?? ''}`,
  leadNo: v.leadNo ?? (v.leadId?.leadNo ?? ''),
  name: v.customer ?? v.customerInfo?.name ?? v.customerId?.name ?? v.name ?? v.lead?.name ?? '-',
  profession: v.customerInfo?.profession ?? v.customerId?.profession ?? v.profession ?? v.lead?.profession ?? 'Not Provided',
  phone: v.mobileNumber ?? v.customerInfo?.phone ?? v.customerId?.phone ?? v.phone ?? v.lead?.contact?.phone ?? v.contact?.phone ?? '-',
  whatsapp: v.whatsappNumber ?? v.customerInfo?.whatsapp ?? v.customerId?.whatsapp ?? v.whatsapp ?? v.lead?.contact?.whatsapp ?? v.contact?.whatsapp ?? '-',
  email: v.customerInfo?.email ?? v.customerId?.email ?? v.email ?? v.lead?.email ?? '',
  interestedIn: v.interestedIn ?? v.lead?.interestedIn ?? '',
  budgetUpto: v.budgetUpto ?? v.lead?.budgetUpto ?? '',
  visitedBy: v.siteVisitBy ?? v.visitedBy ?? v.createdBy ?? '',
  visitedByPhone: v.siteVisitByPhone ?? v.visitedByPhone ?? v.visitedBy?.phone ?? '',
  visitedByEmail: v.visitedByEmail ?? v.visitedBy?.email ?? '',
  associateId: v.siteVisitByAssociateId?._id ?? v.siteVisitByAssociateId ?? v.associateId ?? v.assignedTo?.id ?? null,
  createdBy: v.createdBy ?? '',
  createdByPhone: v.createdByPhone ?? '',
  source: v.source ?? v.lead?.source ?? '',
  leadCreatedOn: v.leadCreatedOn || (v.leadId?.createdAt || v.lead?.createdAt || ''),
  visitCreatedOn: v.visitCreatedOn || v.createdAt || '',
  nextFollowUp: v.nextFollowUpDate || v.followUp?.nextFollowUpDate || '',
  status: v.inquiryStatus ?? v.status ?? 'HOT',
  stage: v.stage ?? 'Stage Visit',
  address: v.address ?? v.customerInfo?.otherDetails?.address ?? v.customerId?.otherDetails?.address ?? v.address?.addressLine ?? v.lead?.address?.addressLine ?? '',
  city: v.customerInfo?.city ?? v.customerId?.city ?? v.address?.city ?? v.lead?.address?.city ?? '',
  state: v.customerInfo?.state ?? v.customerId?.state ?? v.address?.state ?? v.lead?.address?.state ?? '',
  projectInterested: v.projectInterested ?? v.lead?.projectInterested ?? '',
  projectsDetails: v.projectsDetails ?? v.projects ?? [],
  lastFollowUpRemark: v.followUpRemark ?? v.lastFollowUpRemark ?? v.followUp?.lastRemark ?? '',
  referenceName: v.referenceName1 ?? v.referenceName ?? '',
  remark1: v.remark1 ?? v.remarks?.remark1 ?? '',
  visitRemark: v.visitDescription ?? v.visitRemark ?? '',
  remark2: v.remark2 ?? v.remarks?.remark2 ?? '',
  visitSelfies: v.selfies?.length ?? v.visitSelfies?.length ?? 0,
  followUpDate: (v.followUpDate || v.followUp?.lastFollowUpDate) || '',
  followUpTime: (v.followUpDate || v.followUp?.lastFollowUpDate) ? new Date(v.followUpDate || v.followUp?.lastFollowUpDate).toLocaleTimeString() : '-',
  // Store full objects for editing
  customerId: v.customerId ?? v.customerInfo,
  leadIdObj: v.leadId,
  siteVisitByAssociateId: v.siteVisitByAssociateId,
  timelineCount: v.timelineCount ?? 0,
  // Store original visit data for editing
  originalVisitData: v,
});

const displayDate = (dateString) => {
  if (!dateString || dateString === '-' || dateString === '') return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (e) {
    return dateString;
  }
};

const formatNextFollowUp = (dateString) => {
  if (!dateString || dateString === '-' || dateString === '') return { date: '-', status: '', days: '', fullText: '-', color: 'inherit' };
  try {
    let date;
    if (typeof dateString === 'string' && (dateString.includes('T') || dateString.includes('Z'))) {
      date = new Date(dateString);
    } else if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2].split(/[ ,T]/)[0], 10);
        date = new Date(year, month, day);
      } else {
        date = new Date(dateString);
      }
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      return { date: dateString, status: '', days: '', fullText: dateString, color: 'inherit' };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUpDate = new Date(date);
    followUpDate.setHours(0, 0, 0, 0);

    const diffTime = followUpDate.getTime() - today.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    const formattedDate = date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    let status = '';
    let daysText = '';
    let color = 'inherit';

    if (diffDays === 0) {
      status = 'Today Visit';
      daysText = 'Due today';
      color = '#4CAF50';
    } else if (diffDays < 0) {
      const absDays = Math.abs(diffDays);
      if (absDays < 7) {
        daysText = `${absDays} day${absDays > 1 ? 's' : ''} ago`;
      } else if (absDays < 30) {
        const weeks = Math.floor(absDays / 7);
        daysText = `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      } else if (absDays < 365) {
        const months = Math.floor(absDays / 30);
        daysText = `${months} month${months > 1 ? 's' : ''} ago`;
      } else {
        const years = Math.floor(absDays / 365);
        daysText = `${years} year${years > 1 ? 's' : ''} ago`;
      }
      status = 'Past Due';
      color = '#FF7043';
    } else if (diffDays === 1) {
      status = 'Upcoming Visit';
      daysText = 'Tomorrow';
      color = '#40e609ff';
    } else if (diffDays < 7) {
      status = 'Upcoming Visit';
      daysText = `In ${diffDays} days`;
      color = '#40e609ff';
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      daysText = `In ${weeks} week${weeks > 1 ? 's' : ''}`;
      status = 'Upcoming Visit';
      color = '#40e609ff';
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      daysText = `In ${months} month${months > 1 ? 's' : ''}`;
      status = 'Upcoming Visit';
      color = '#40e609ff';
    } else {
      const years = Math.floor(diffDays / 365);
      daysText = `In ${years} year${years > 1 ? 's' : ''}`;
      status = 'Upcoming Visit';
      color = '#40e609ff';
    }

    return {
      date: formattedDate,
      status: status,
      days: daysText,
      fullText: `${formattedDate} (${daysText})`,
      color: color
    };
  } catch {
    return { date: dateString, status: '', days: '', fullText: dateString, color: 'inherit' };
  }
};

const getStatusColor = (status, dynamicColors = {}) => {
  const s = (status || '').toUpperCase();

  // Check if dynamic color exists
  if (dynamicColors[s]) {
    return { backgroundColor: dynamicColors[s], color: '#fff' };
  }

  switch (s) {
    case 'NEW':
    case 'CREATED': return { backgroundColor: '#1976d2', color: '#fff' };
    case 'CONTACTED': return { backgroundColor: '#f57c00', color: '#fff' };
    case 'QUALIFIED': return { backgroundColor: '#2e7d32', color: '#fff' };
    case 'CONVERTED': return { backgroundColor: '#7b1fa2', color: '#fff' };
    case 'HOT': return { backgroundColor: '#d32f2f', color: '#fff' };
    case 'WARM': return { backgroundColor: '#ed6c02', color: '#fff' };
    case 'COLD': return { backgroundColor: '#0288d1', color: '#fff' };
    case 'INTERESTED': return { backgroundColor: '#2e7d32', color: '#fff' };
    case 'NOT INTERESTED': return { backgroundColor: '#455a64', color: '#fff' };
    case 'UPDATED': return { backgroundColor: '#ab47bc', color: '#fff' };
    case 'SCHEDULED': return { backgroundColor: '#29b6f6', color: '#fff' };
    case 'IN PROGRESS': return { backgroundColor: '#ffa726', color: '#fff' };
    case 'READY TO VISIT': return { backgroundColor: '#4caf50', color: '#fff' };
    default: return { backgroundColor: '#9e9e9e', color: '#fff' };
  }
};

// Reset Button Component
const ResetButton = ({ onClick, disabled }) => (
  <Button
    variant="outlined"
    color="error"
    startIcon={<RefreshIcon />}
    onClick={onClick}
    disabled={disabled}
    fullWidth
    sx={{
      height: '56px',
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '12px',
      borderRadius: 2,
      borderColor: '#d32f2f',
      '&:hover': {
        borderColor: '#b71c1c',
        backgroundColor: 'rgba(211, 47, 47, 0.04)',
      },
    }}
  >
    Reset
  </Button>
);


const SiteVisit = () => {
  const canRead = hasReadPermission('visit-site.visit-site');
  const canWrite = hasWritePermission('visit-site.visit-site');
  const history = useHistory();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusOptions, setStatusOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedVisits, setSelectedVisits] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    inquiryStatus: '',
    followUpRemark: '',
    nextFollowUpDate: '',
  });
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsSaveError, setDetailsSaveError] = useState('');
  const [detailsSaveSuccess, setDetailsSaveSuccess] = useState('');
  const [cardTabValue, setCardTabValue] = useState({});
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    followUpDate: '',
    nextFollowUpDate: '',
    inquiryStatus: '',
    remark: '',
    visitType: '',
  });
  const [followUpHistory, setFollowUpHistory] = useState([]);
  const [followUpError, setFollowUpError] = useState('');
  const [followUpSuccess, setFollowUpSuccess] = useState('');
  const [followUpLoading, setFollowUpLoading] = useState(false);
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [timelineSelectedVisit, setTimelineSelectedVisit] = useState(null);
  const [timelineSelectedAssociate, setTimelineSelectedAssociate] = useState('');
  const [timelineSelectedVisitId, setTimelineSelectedVisitId] = useState('');
  const [timelineHasSearched, setTimelineHasSearched] = useState(false);
  const [associateFilter, setAssociateFilter] = useState(null);
  const [associateDirectory, setAssociateDirectory] = useState([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const [sortOrder, setSortOrder] = useState('all');
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [moreFiltersSortBy, setMoreFiltersSortBy] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');
  const [tempDateFilter, setTempDateFilter] = useState('');


  const loadAssociatesList = useCallback(async (searchTerm = '') => {
    setLoadingAssociates(true);
    try {
      const params = {};
      if (searchTerm) {
        params.filterKey = searchTerm;
      }
      const response = await getAssociates(params);
      const associates = Array.isArray(response?.data)
        ? response.data
        : (response?.data?.docs || response?.docs || []);

      const formattedAssociates = associates.map(associate => ({
        id: associate._id,
        associateId: associate.associateId || associate.associatedId || associate._id,
        name: associate.name || '',
        mobile: associate.phone || associate.mobile || '',
        displayLabel: `${associate.name || ''} — ${associate.phone || associate.mobile || ''}`,
      }));

      setAssociateDirectory(formattedAssociates);
    } catch (error) {
      console.error("Error fetching associates:", error);
    } finally {
      setLoadingAssociates(false);
    }
  }, []);

  const handleAssociateInputChange = (event, newInputValue) => {
    setAssociateSearchInput(newInputValue);
    loadAssociatesList(newInputValue);
  };

  const handleAssociateOpen = () => {
    if (associateDirectory.length === 0) {
      loadAssociatesList();
    }
  };

  const [importSuccess, setImportSuccess] = useState('');
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [statusColors, setStatusColors] = useState({});

  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: '',
    cancelText: '',
    type: 'warning',
    action: null
  });

  const showAlert = (message, type = 'info') => setAlertData({ open: true, message, type });

  const handleImportClick = () => {
    history.push('/app/siteVisit/bulk-import');
  };



  const handleOpenExportMenu = (event) => setExportMenuAnchorEl(event.currentTarget);
  const handleCloseExportMenu = () => setExportMenuAnchorEl(null);

  const fetchSiteVisits = useCallback(async (pageNum, limit, query, tab, status, source, associateId, sortVal, fromDateVal, toDateVal, dateFilterVal) => {
    setLoading(true);
    setError('');

    try {
      // Build API params
      const params = {
        page: pageNum ?? page,
        limit: limit ?? rowsPerPage,
      };

      // Add search query if provided
      const searchQuery = query !== undefined ? query : search;
      if (searchQuery) {
        params.filterKey = searchQuery;
      }

      // Add tab-based filters 
      const activeTab = tab !== undefined ? tab : tabValue;
      if (activeTab === 1) {
        // Today Booking Followup
        params.visitType = 'today';
      } else if (activeTab === 2) {
        // Pending Booking Followup
        params.visitType = 'pending';
      }

      // Add inquiry status filter
      const activeStatus = status !== undefined ? status : statusFilter;
      if (activeStatus && activeStatus !== 'all') {
        params.inquiryStatus = activeStatus;
      }

      // Add source filter
      const activeSource = source !== undefined ? source : sourceFilter;
      if (activeSource && activeSource !== 'all') {
        params.source = activeSource;
      }

      // Add associate ID filter
      const activeAssociateId = associateId !== undefined ? associateId : associateFilter?.id;
      if (activeAssociateId) {
        params.associateId = activeAssociateId;
      }

      // Add sort order filter
      const activeSortOrder = sortVal !== undefined ? sortVal : sortOrder;
      if (activeSortOrder && activeSortOrder !== 'all') {
        if (activeSortOrder === 'timelineCountDesc') {
          params.sortBy = 'timelineCount';
          params.sortOrder = 'desc';
        } else if (activeSortOrder === 'timelineCountAsc') {
          params.sortBy = 'timelineCount';
          params.sortOrder = 'asc';
        } else {
          params.sortOrder = activeSortOrder;
        }
      }

      // Add date filters
      const activeFromDate = fromDateVal !== undefined ? fromDateVal : fromDate;
      const activeToDate = toDateVal !== undefined ? toDateVal : toDate;
      const activeDateFilter = dateFilterVal !== undefined ? dateFilterVal : dateFilter;
      if (activeFromDate) params.fromDate = activeFromDate;
      if (activeToDate) params.toDate = activeToDate;
      if (activeDateFilter) params.date = activeDateFilter;

      const response = await siteVisitService.getSiteVisits(params);

      // Handle different response structures
      let visits = [];
      let total = 0;

      if (response.success) {
        // If response has success flag
        visits = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.data?.totalDocs || response.data?.total || response.count || visits.length;
      } else if (Array.isArray(response)) {
        // If response is directly an array
        visits = response;
        total = response.length;
      } else if (response.data) {
        // If response has data property
        visits = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
        total = response.data?.totalDocs || response.data?.total || response.count || visits.length;
      } else {
        visits = [];
        total = 0;
      }

      // Map the API response to the expected format
      // Pass the full visit object to mapSiteVisit so it has access to customerId, leadId, etc.
      const mappedVisits = visits.map((visit) => mapSiteVisit(visit));
      setData(mappedVisits);
      setTotalDocs(total);
    } catch (e) {
      console.error('Error fetching site visits:', e);
      setError(e?.message || 'Failed to load site visits');
      setData([]);
      setTotalDocs(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter, sortOrder, fromDate, toDate, dateFilter]);

  const fetchStatusOptions = async () => {
    try {
      const inquiryStatuses = await getInquiryStatuses();
      // Normalize to uppercase, trim, and remove duplicates
      const options = [...new Set(inquiryStatuses.map(s => (s.statusName || '').trim().toUpperCase()).filter(Boolean))];
      setStatusOptions(options);

      // Create a map of status name to color code
      const colorMap = {};
      inquiryStatuses.forEach(s => {
        if (s.statusName) {
          colorMap[s.statusName.trim().toUpperCase()] = s.colorCode;
        }
      });
      setStatusColors(colorMap);
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const fetchSourceOptions = async () => {
    try {
      const sources = await getSources();
      setSourceOptions(sources || []);
    } catch (error) {
      console.error("Error fetching source options:", error);
    }
  };

  useEffect(() => {
    fetchStatusOptions();
    fetchSourceOptions();
  }, []);

  // Initial load and updates for page/rows/tab/filter changes
  useEffect(() => {
    fetchSiteVisits(page, rowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter?.id, sortOrder, fromDate, toDate, dateFilter);
  }, [page, rowsPerPage, tabValue, statusFilter, sourceFilter, associateFilter, sortOrder, fetchSiteVisits]);

  const handleRefresh = () => {
    fetchSiteVisits(page, rowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter?.id, sortOrder);
  };

  // Search is now handled manually via button or Enter key
  // Removed auto-search debounce to allow manual search control

  const handleChangePage = (_, newPage) => {
    const pageNum = newPage + 1;
    setPage(pageNum);
    fetchSiteVisits(pageNum, rowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter?.id, sortOrder, fromDate, toDate, dateFilter);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    fetchSiteVisits(1, newRowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter?.id, sortOrder, fromDate, toDate, dateFilter);
  };



  const exportRows = useMemo(() => {
    return (data || []).map((v) => ({
      "Visit No": v.visitNo || "-",
      "Lead No": v.leadNo || "-",
      "Name": v.name || "-",
      "Phone": v.phone || "-",
      "Interest": v.interestedIn || "-",
      "Budget": v.budgetUpto || "-",
      "Visit Date": displayDate(v.visitCreatedOn) || "-",
      "Next FollowUp": formatNextFollowUp(v.nextFollowUp).date || "-",
      "Status": v.status || "-",
      "Assigned To": v.visitedBy || "-",
      "Remark": v.visitRemark || v.lastFollowUpRemark || "-",
    }));
  }, [data]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSearchClick = () => {
    setPage(1);
    fetchSiteVisits(1, rowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter?.id, sortOrder, fromDate, toDate, dateFilter);
  };

  const handleReset = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
    setSourceFilter('all');
    setAssociateFilter(null);
    setSortOrder('all');
    setMoreFiltersSortBy('all');
    setFromDate('');
    setToDate('');
    setDateFilter('');
    setTempFromDate('');
    setTempToDate('');
    setTempDateFilter('');
    setTabValue(0);
    setPage(1);
    fetchSiteVisits(1, rowsPerPage, '', 0, 'all', 'all', null, 'all', '', '', '');
  }, [rowsPerPage, fetchSiteVisits]);

  const handleMoreFiltersApply = () => {
    setSortOrder(moreFiltersSortBy);
    setFromDate(tempFromDate);
    setToDate(tempToDate);
    setDateFilter(tempDateFilter);
    setPage(1);
    fetchSiteVisits(1, rowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter?.id, moreFiltersSortBy, tempFromDate, tempToDate, tempDateFilter);
  };

  const handleMoreFiltersReset = () => {
    setMoreFiltersSortBy('all');
    setSortOrder('all');
    setFromDate('');
    setToDate('');
    setDateFilter('');
    setTempFromDate('');
    setTempToDate('');
    setTempDateFilter('');
    setPage(1);
    fetchSiteVisits(1, rowsPerPage, search, tabValue, statusFilter, sourceFilter, associateFilter?.id, 'all', '', '', '');
  };


  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedVisits(data.map((v) => v.id));
      setSelectAll(true);
    } else {
      setSelectedVisits([]);
      setSelectAll(false);
    }
  };

  const handleSelectVisit = (visitId) => {
    setSelectedVisits((prev) =>
      prev.includes(visitId)
        ? prev.filter((id) => id !== visitId)
        : [...prev, visitId]
    );
    setSelectAll(false);
  };

  const handleDelete = async (visit) => {
    setConfirmDialog({
      open: true,
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this site visit?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'error',
      action: async () => {
        try {
          // Call delete API (assuming it exists in the service)
          // For now, we'll just refresh the list after deletion
          // TODO: Add deleteSiteVisit method to siteVisitService if needed
          setData((prev) => prev.filter((item) => item.id !== visit.id));
          showAlert('Site visit deleted successfully', 'Success');
          // Refresh the list
          fetchSiteVisits(page, rowsPerPage, search, tabValue, statusFilter);
        } catch (error) {
          console.error('Error deleting site visit:', error);
          showAlert(`Failed to delete site visit: ${error.message}`, 'Error');
        }
      }
    });
  };

  const handleDeleteAll = async () => {
    if (selectedVisits.length === 0) {
      showAlert('Please select at least one site visit to delete', 'Warning');
      return;
    }

    setConfirmDialog({
      open: true,
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete ${selectedVisits.length} site visit(s)?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'error',
      action: async () => {
        try {
          // Call delete API for each selected visit
          // TODO: Add bulk delete API if available
          setData((prev) => prev.filter((item) => !selectedVisits.includes(item.id)));
          setSelectedVisits([]);
          setSelectAll(false);
          fetchSiteVisits(page, rowsPerPage, search, tabValue, statusFilter);
          showAlert('Selected site visits deleted successfully', 'Success');
        } catch (error) {
          console.error('Error deleting site visits:', error);
          showAlert(`Failed to delete site visits: ${error.message}`, 'Error');
        }
      }
    });
  };

  const handleExportExcel = () => {
    if (!exportRows.length) {
      handleCloseExportMenu();
      return;
    }
    exportAsExcel({ rows: exportRows, sheetName: 'SiteVisits', fileNameBase: 'site_visits' });
    handleCloseExportMenu();
  };

  const handleExportCSV = () => {
    if (!exportRows.length) {
      handleCloseExportMenu();
      return;
    }
    exportAsCSV({ rows: exportRows, fileNameBase: 'site_visits' });
    handleCloseExportMenu();
  };

  const handleExportPDF = () => {
    if (!exportRows.length) {
      handleCloseExportMenu();
      return;
    }
    const headers = Object.keys(exportRows[0] || {});
    exportAsPDF({
      rows: exportRows,
      headers,
      title: 'Site Visit List',
      fileNameBase: 'site_visits',
      headStyles: { fillColor: [83, 109, 254] },
    });
    handleCloseExportMenu();
  };
  const handleViewDetails = (visit) => {
    setSelectedVisit(visit);
    // Initialize edit form data from visit
    // Parse nextFollowUpDate - handle both ISO string and formatted date
    let nextFollowUpDateValue = '';
    if (visit.nextFollowUp && visit.nextFollowUp !== '-') {
      try {
        // Try to parse the date - it might be ISO string or formatted date
        const date = new Date(visit.nextFollowUp);
        if (!isNaN(date.getTime())) {
          // Convert to datetime-local format (YYYY-MM-DDTHH:mm)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          nextFollowUpDateValue = `${year}-${month}-${day}T${hours}:${minutes}`;
        }
      } catch (e) {
        console.error('Error parsing nextFollowUpDate:', e);
      }
    }

    setEditFormData({
      inquiryStatus: visit.status || visit.inquiryStatus || '',
      followUpRemark: visit.lastFollowUpRemark || visit.followUpRemark || '',
      nextFollowUpDate: nextFollowUpDateValue,
    });
    setDetailsSaveError('');
    setDetailsSaveSuccess('');
    setDetailsDialogOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveDetails = async () => {
    if (!selectedVisit?.id) return;

    setSavingDetails(true);
    setDetailsSaveError('');
    setDetailsSaveSuccess('');

    try {
      // Get current user info for updatedBy
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Format nextFollowUpDate to ISO string
      const nextFollowUpDate = editFormData.nextFollowUpDate
        ? new Date(editFormData.nextFollowUpDate).toISOString()
        : '';

      const payload = {
        inquiryStatus: editFormData.inquiryStatus,
        followUpRemark: editFormData.followUpRemark,
        nextFollowUpDate: nextFollowUpDate,
        updatedBy: currentUserName,
      };

      const response = await siteVisitService.updateSiteVisit(selectedVisit.id, payload);

      if (response.success) {
        setDetailsSaveSuccess('Site visit updated successfully!');
        // Refresh the list
        fetchSiteVisits(page, rowsPerPage, search, tabValue, statusFilter);
        // Update selected visit to reflect changes
        setSelectedVisit((prev) => ({
          ...prev,
          status: editFormData.inquiryStatus,
          inquiryStatus: editFormData.inquiryStatus,
          lastFollowUpRemark: editFormData.followUpRemark,
          followUpRemark: editFormData.followUpRemark,
          nextFollowUp: editFormData.nextFollowUpDate
            ? new Date(editFormData.nextFollowUpDate).toLocaleDateString()
            : prev.nextFollowUp,
          nextFollowUpDate: nextFollowUpDate,
        }));
        setTimeout(() => {
          setDetailsSaveSuccess('');
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to update site visit');
      }
    } catch (error) {
      console.error('Error updating site visit:', error);
      setDetailsSaveError(error.message || 'Failed to update site visit');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleEdit = (visit) => {
    // Pass the full visit object including customerId and leadId objects from original API response
    const originalData = visit.originalVisitData || visit;
    history.push('/app/siteVisit/AddSiteVisit', {
      visitData: {
        ...visit,
        // Include the full customerId and leadId objects from the original API response
        customerId: originalData.customerId ?? originalData.customerInfo,
        leadId: originalData.leadId,
        siteVisitByAssociateId: originalData.siteVisitByAssociateId,
        projectsDetails: originalData.projectsDetails || originalData.projects || [],
        // Include nextFollowUpDate from original data (raw ISO string)
        nextFollowUpDate: originalData.nextFollowUpDate || visit.nextFollowUpDate,
      }
    });
  };

  const handleAddNew = () => {
    history.push('/app/siteVisit/AddSiteVisit');
  };

  const handleBooking = (visit) => {
    // Extract customer ID accurately from either the ID field or the object
    const actualCustomerId = visit.customerId?._id || visit.customerId?.id || visit.customerId;

    // Construct a minimal associate data object to help with auto-fill in BookPlot
    const associateData = visit.associateId ? {
      id: visit.associateId,
      name: visit.visitedBy || "",
      phone: visit.visitedByPhone || ""
    } : null;

    history.push('/app/booking/AddBooking', {
      leadData: {
        ...visit,
        name: visit.name === '-' ? '' : visit.name,
        phone: visit.phone === '-' ? '' : visit.phone,
        customerId: actualCustomerId
      },
      visitId: visit.id || visit._id || null,
      associateData: associateData
    });
  };

  const handleFollowUp = async (visit) => {
    setSelectedVisit(visit);
    setFollowUpDialogOpen(true);
    setFollowUpData({
      followUpDate: '',
      nextFollowUpDate: '',
      inquiryStatus: '',
      remark: '',
      visitType: '',
    });
    setFollowUpError('');
    setFollowUpSuccess('');
    setFollowUpLoading(true);
    setFollowUpHistory([]);

    try {
      // Fetch follow-up history from API
      const params = {
        siteVisitId: visit.id,
        page: 1,
        limit: 10,
      };

      const response = await siteVisitService.getSiteVisitFollowUps(params);

      // Handle different response structures
      let followUps = [];
      if (response.success) {
        followUps = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
      } else if (Array.isArray(response)) {
        followUps = response;
      } else if (response.data) {
        followUps = Array.isArray(response.data) ? response.data : (response.data?.docs || []);
      }

      // Map API response to UI format - preserve original order from API (no sorting)
      const mappedHistory = followUps.map((followUp) => {
        const followUpDate = followUp.followUpDate
          ? new Date(followUp.followUpDate).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }).replace(',', '')
          : '';

        const nextScheduleDate = followUp.nextFollowUpDate
          ? new Date(followUp.nextFollowUpDate).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          }).replace(',', '')
          : '';

        return {
          id: followUp._id || followUp.id || Date.now(),
          type: followUp.followUpType || 'Visit',
          followUpDate: followUpDate,
          remark: followUp.followUpRemark || followUp.remark || '',
          nextScheduleDate: nextScheduleDate,
          status: followUp.inquiryStatus || followUp.status || 'HOT',
          createdBy: followUp.createdBy || followUp.followedUpBy || 'Yes',
        };
      });

      setFollowUpHistory(mappedHistory);
    } catch (error) {
      console.error('Error fetching follow-up history:', error);
      setFollowUpError('Failed to load follow-up history. Please try again.');
      setFollowUpHistory([]);
    } finally {
      setFollowUpLoading(false);
    }
  };

  const handleFollowUpFormChange = (field, value) => {
    setFollowUpData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFollowUpSave = async () => {
    const { followUpDate, nextFollowUpDate, inquiryStatus, remark, visitType } = followUpData;
    if (!followUpDate || !nextFollowUpDate || !inquiryStatus || !remark || !visitType) {
      setFollowUpError('Please fill all required fields.');
      return;
    }

    if (!selectedVisit?.id) {
      setFollowUpError('Site visit information is missing.');
      return;
    }

    setFollowUpLoading(true);
    setFollowUpError('');
    setFollowUpSuccess('');

    try {
      // Get current user info for createdBy
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName = userData?.user?.name || userData?.name || userData?.username || 'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Convert dates to ISO format
      const followUpDateObj = new Date(followUpDate);
      const nextFollowUpDateObj = new Date(nextFollowUpDate);

      // Extract time from followUpDate for followUpTime (format: "10:00 AM")
      const hours = followUpDateObj.getHours();
      const minutes = followUpDateObj.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      const followUpTime = `${displayHours}:${displayMinutes} ${ampm}`;

      // Build API payload using only fields from current form
      const payload = {
        siteVisitId: selectedVisit.id,
        followUpDate: followUpDateObj.toISOString(),
        followUpTime: followUpTime,
        followUpType: visitType || 'Call',
        visitType: visitType,
        followedUpBy: selectedVisit.visitedBy || currentUserName,
        followedUpByPhone: selectedVisit.visitedByPhone || '',
        followedUpByAssociateId: selectedVisit.associateId || null,
        followUpRemark: remark,
        inquiryStatus: inquiryStatus,
        nextFollowUpDate: nextFollowUpDateObj.toISOString(),
        nextFollowUpRemark: remark, // Using same remark for next follow-up
        createdBy: currentUserName,
      };

      const response = await siteVisitService.createSiteVisitFollowUp(payload);

      // Check for success - response can have status: true or success: true
      const isSuccess = response.status === true || response.success === true || response._id || response.data?._id;

      if (isSuccess) {
        const successMessage = response.message || 'Follow-up saved successfully!';
        setFollowUpSuccess(successMessage);

        // Reset form
        setFollowUpData({
          followUpDate: '',
          nextFollowUpDate: '',
          inquiryStatus: '',
          remark: '',
          visitType: '',
        });

        // Immediately call GET API to refresh follow-up history and show the newly added follow-up
        // Add a small delay to ensure backend has processed the new follow-up
        await new Promise(resolve => setTimeout(resolve, 100));

        try {
          const params = {
            siteVisitId: selectedVisit.id,
            page: 1,
            limit: 10,
          };

          console.log('Refreshing follow-up history for siteVisitId:', selectedVisit.id);
          const historyResponse = await siteVisitService.getSiteVisitFollowUps(params);

          let followUps = [];
          if (historyResponse.success || historyResponse.status === true) {
            followUps = Array.isArray(historyResponse.data) ? historyResponse.data : (historyResponse.data?.docs || []);
          } else if (Array.isArray(historyResponse)) {
            followUps = historyResponse;
          } else if (historyResponse.data) {
            followUps = Array.isArray(historyResponse.data) ? historyResponse.data : (historyResponse.data?.docs || []);
          }

          console.log('Fetched follow-ups:', followUps.length);

          // Map API response to UI format - preserve original order from API (no sorting)
          const mappedHistory = followUps.map((followUp) => {
            const followUpDate = followUp.followUpDate
              ? new Date(followUp.followUpDate).toLocaleString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }).replace(',', '')
              : '';

            const nextScheduleDate = followUp.nextFollowUpDate
              ? new Date(followUp.nextFollowUpDate).toLocaleString('en-GB', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              }).replace(',', '')
              : '';

            return {
              id: followUp._id || followUp.id || Date.now(),
              type: followUp.followUpType || 'Visit',
              followUpDate: followUpDate,
              remark: followUp.followUpRemark || followUp.remark || '',
              nextScheduleDate: nextScheduleDate,
              status: followUp.inquiryStatus || followUp.status || 'HOT',
              createdBy: followUp.createdBy || followUp.followedUpBy || 'Yes',
            };
          });

          // Update the history with fresh data from API - this will show the new follow-up instantly
          console.log('Updating follow-up history with', mappedHistory.length, 'items');
          setFollowUpHistory(mappedHistory);
        } catch (historyError) {
          console.error('Error refreshing follow-up history:', historyError);
          // Show error but don't fail the entire save operation
          setFollowUpError('Follow-up saved, but failed to refresh history. Please refresh the page.');
        }

        // Refresh site visits list to update next follow-up dates (don't await, let it run in background)
        fetchSiteVisits(page, rowsPerPage, search, tabValue, statusFilter);

        // Clear success message after a delay
        setTimeout(() => {
          setFollowUpSuccess('');
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to save follow-up');
      }
    } catch (error) {
      console.error('Error saving follow-up:', error);
      setFollowUpError(error.message || 'Failed to save follow-up. Please try again.');
    } finally {
      setFollowUpLoading(false);
    }
  };

  const getAvatarInitials = (name) => {
    const parts = (name || '').trim().split(' ');
    if (parts.length === 1) return (name?.charAt(0) || '').toUpperCase() + (name?.charAt(1) || '').toUpperCase();
    const first = parts[0] || '';
    const last = parts[parts.length - 1] || '';
    return first.charAt(0).toUpperCase() + last.charAt(0).toUpperCase();
  };

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2', '#0097a7', '#c2185b', '#5d4037', '#455a64', '#00796b'];
    return colors[(name?.charCodeAt(0) || 0) % colors.length];
  };


  const getPriorityColor = (priority) => {
    if (!priority) return '#9e9e9e';
    const normalizedPriority = priority.trim().toUpperCase();

    // First check in the dynamic status colors from API
    if (statusColors[normalizedPriority]) {
      return statusColors[normalizedPriority];
    }

    // Fallback to existing hardcoded colors for backward compatibility
    switch (priority) {
      case 'High': return '#f44336';
      case 'Medium': return '#ff9800';
      case 'Low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };


  const handleCardTabChange = (visitId, newValue) => {
    setCardTabValue((prev) => ({ ...prev, [visitId]: newValue }));
  };

  const getCardTabValue = (visitId) => cardTabValue[visitId] || 0;

  const formatDateWithRelative = (dateString) => {
    if (!dateString || dateString === '-') return dateString;
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const months = Math.floor(diffDays / 30);

      let relativeText = '';
      if (months > 0) {
        relativeText = `${months} month${months > 1 ? 's' : ''} ago`;
      } else if (diffDays > 0) {
        relativeText = `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      } else {
        relativeText = 'Today';
      }

      return `${date.toLocaleDateString('en-GB')} | ${relativeText}`;
    } catch {
      return dateString;
    }
  };

  const formatDateFull = (dateString) => {
    if (!dateString || dateString === '-') return dateString;
    try {
      const date = new Date(dateString);
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dayName = days[date.getDay()];
      return `${dayName}, ${date.toLocaleDateString('en-GB')}`;
    } catch {
      return dateString;
    }
  };

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view the Site Visit details. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1, backgroundColor: '#f9f9fb', minHeight: '100vh' }}>
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Site Visit
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and track all your site visits
            </Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
            {canWrite && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<ImportIcon />}
                sx={{ fontWeight: 600, px: 2, textTransform: 'none', fontSize: '14px', borderRadius: 2, height: { xs: 'auto', sm: 36 } }}
                onClick={handleImportClick}
              >
                Import
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<ExportIcon />}
              sx={{ fontWeight: 600, px: 2, textTransform: 'none', fontSize: '14px', borderRadius: 2, height: { xs: 'auto', sm: 36 } }}
              onClick={handleOpenExportMenu}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportMenuAnchorEl}
              open={Boolean(exportMenuAnchorEl)}
              onClose={handleCloseExportMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleExportExcel}>Export as Excel</MenuItem>
              <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
              <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
            </Menu>
            {canWrite && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddNew}
                startIcon={<AddIcon />}
                sx={{
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2,
                  height: { xs: 'auto', sm: 36 }
                }}
              >
                Add New
              </Button>
            )}
            <RefreshButton
              onClick={handleRefresh}
              loading={loading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '14px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 36 }
              }}
            />
          </Box>
        </Box>
      </Box>

      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3} md={2.5}>
              <TextField
                fullWidth
                placeholder="Search name, phone, email..."
                value={search}
                onChange={handleSearchChange}
                onKeyPress={handleSearchKeyPress}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: '56px' } }}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={1.2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearchClick}
                startIcon={<SearchIcon />}
                fullWidth
                sx={{
                  fontWeight: 600,
                  height: '56px',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: 2,
                }}
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} sm={2} md={1.8}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Status"
                  sx={{ borderRadius: 2, height: '56px' }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {statusOptions.map(option => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                  {/* Fallback items in case API fails or returns incomplete list */}
                  {!statusOptions.includes('HOT') && <MenuItem value="HOT">HOT</MenuItem>}
                  {!statusOptions.includes('WARM') && <MenuItem value="WARM">WARM</MenuItem>}
                  {!statusOptions.includes('COLD') && <MenuItem value="COLD">COLD</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2} md={1.8}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  onChange={(e) => {
                    setSourceFilter(e.target.value);
                    setPage(1);
                  }}
                  label="Source"
                  sx={{ borderRadius: 2, height: '56px' }}
                >
                  <MenuItem value="all">All Sources</MenuItem>
                  {sourceOptions.map((option) => (
                    <MenuItem key={option.id} value={option.name}>
                      {option.name}
                    </MenuItem>
                  ))}
                  {/* Fallback items in case API fails or returns incomplete list */}
                  {sourceOptions.length === 0 && (
                    ['Website', 'Reference', 'Walk-in', 'Social Media', 'Advertisement', 'Cold Call', 'Digital Marketing'].map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3} md={2.0}>
              <Autocomplete
                key={associateDirectory.length > 0 ? 'loaded' : 'empty'}
                fullWidth
                options={associateDirectory}
                getOptionLabel={(option) => option.displayLabel || `${option.name || ''} — ${option.mobile || ''}`}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                loading={loadingAssociates}
                inputValue={associateSearchInput}
                onInputChange={handleAssociateInputChange}
                onOpen={handleAssociateOpen}
                value={associateFilter}
                onChange={(event, newValue) => {
                  setAssociateFilter(newValue);
                  setPage(1);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associate"
                    placeholder="Search Associate"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAssociates ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={1.5}>
              <MoreFilters
                expanded={moreFiltersOpen}
                onToggle={() => setMoreFiltersOpen(prev => !prev)}
                sortBy={moreFiltersSortBy}
                onChangeSortBy={setMoreFiltersSortBy}
                onApply={handleMoreFiltersApply}
                onReset={handleMoreFiltersReset}
                type="siteVisit"
                height="56px"
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="From Date"
                      value={tempFromDate}
                      onChange={(e) => {
                        setTempFromDate(e.target.value);
                        if (e.target.value) setTempDateFilter('');
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          height: '44px',
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: '#e0e0e0' },
                          '&:hover fieldset': { borderColor: '#536DFE' },
                          '&.Mui-focused fieldset': { borderColor: '#536DFE' },
                        },
                        '& .MuiOutlinedInput-input': { fontSize: '13px' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="To Date"
                      value={tempToDate}
                      onChange={(e) => {
                        setTempToDate(e.target.value);
                        if (e.target.value) setTempDateFilter('');
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          height: '44px',
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: '#e0e0e0' },
                          '&:hover fieldset': { borderColor: '#536DFE' },
                          '&.Mui-focused fieldset': { borderColor: '#536DFE' },
                        },
                        '& .MuiOutlinedInput-input': { fontSize: '13px' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Single Date"
                      value={tempDateFilter}
                      onChange={(e) => {
                        setTempDateFilter(e.target.value);
                        if (e.target.value) {
                          setTempFromDate('');
                          setTempToDate('');
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          height: '44px',
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: '#e0e0e0' },
                          '&:hover fieldset': { borderColor: '#536DFE' },
                          '&.Mui-focused fieldset': { borderColor: '#536DFE' },
                        },
                        '& .MuiOutlinedInput-input': { fontSize: '13px' },
                      }}
                    />
                  </Grid>
                </Grid>
              </MoreFilters>
            </Grid>
            <Grid item xs={12} sm={2} md={1.2}>
              <ResetButton onClick={handleReset} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => {
            setTabValue(v);
            setPage(1);
            fetchSiteVisits(1, rowsPerPage, search, v, statusFilter, sourceFilter);
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '15px', minHeight: 56 },
          }}
        >
          <Tab label={`All Visits (${totalDocs})`} />
          <Tab label="Today Booking Followup" />
          <Tab label="Pending Booking Followup" />
        </Tabs>
      </Box>

      <Loader open={loading} message="Loading site visits…" />

      {!!error && (
        <Paper sx={{ p: 2, mb: 2, border: '1px solid #f5c2c7', bgcolor: '#f8d7da', color: '#842029', borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>Error: {error}</Typography>
          <Button onClick={() => fetchSiteVisits(page, rowsPerPage, search, tabValue, statusFilter, sourceFilter)} size="small" variant="outlined" startIcon={<RefreshIcon />}>
            Retry
          </Button>
        </Paper>
      )}

      {data.length === 0 && !loading && !error && (
        <NoRecordFound
          message="We couldn't find any site visits matching your current selection."
        />
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {data.map((visit) => (
          <Grid item xs={12} key={visit.id}>
            <Card
              sx={{
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(0,0,0,0.12)' },
              }}
            >
              <Box sx={{ p: 1.5, backgroundColor: '#536DFE', color: 'white' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={visit.timelineCount > 0 ? 2 : 3}>
                    <Box display="flex" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-start' }} gap={1}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                        Visit ID:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '16px' }}>
                        {visit.visitId}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={visit.timelineCount > 0 ? 5 : 6}>
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'center' }} gap={0.5}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                        Next Follow Up:
                      </Typography>
                      {visit.nextFollowUp && visit.nextFollowUp !== '-' ? (
                        <Box display="flex" alignItems="center" gap={0.5} flexWrap="nowrap" justifyContent="flex-end">
                          <FollowUpIcon sx={{ fontSize: 16, color: formatNextFollowUp(visit.nextFollowUp).color }} />
                          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '13px', color: formatNextFollowUp(visit.nextFollowUp).color }}>
                            {formatNextFollowUp(visit.nextFollowUp).fullText}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '13px' }}>
                          -
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  {visit.timelineCount > 0 && (
                    <Grid item xs={12} sm={2.5}>
                      <Box display="flex" flexDirection="row" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'center' }} gap={0.5}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                          Timeline:
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <TimelineIcon sx={{ fontSize: 14 }} />
                          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '14px' }}>
                            {visit.timelineCount}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={visit.timelineCount > 0 ? 2.5 : 3}>
                    <Box display="flex" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }} gap={1}>
                      <Typography variant="body2" sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                        Status:
                      </Typography>
                      <Chip
                        label={visit.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(visit.status, statusColors).backgroundColor,
                          color: getStatusColor(visit.status, statusColors).color,
                          fontWeight: 700,
                          fontSize: '11px',
                          height: '24px',
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
              <CardContent sx={{ pt: 2, pb: 1.5, px: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
                      <Box sx={{ flexShrink: 0 }}>
                        <Avatar
                          alt={visit.name}
                          sx={{
                            width: 100,
                            height: 100,
                            border: '3px solid #e0e0e0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            backgroundColor: getAvatarColor(visit.name),
                            fontSize: '2rem',
                            fontWeight: 700,
                          }}
                        >
                          {getAvatarInitials(visit.name)}
                        </Avatar>
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#000', mb: 0.5, fontSize: '1.15rem', lineHeight: 1.3 }}>
                          {visit.name}
                        </Typography>
                        {/* <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px', mb: 0.7, lineHeight: 1.4 }}>
                          {visit.createdBy}
                        </Typography> */}
                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={visit.status}
                            sx={{
                              height: '24px',
                              backgroundColor: getStatusColor(visit.status, statusColors).backgroundColor,
                              color: getStatusColor(visit.status, statusColors).color,
                              fontWeight: 700,
                              fontSize: '11px',
                            }}
                          />
                          <Box display="flex" alignItems="center">
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                sx={{
                                  fontSize: 14,
                                  color: i < 3 ? '#FFC107' : '#E0E0E0',
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                        <Divider sx={{ my: 1 }} />
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.4 }}>
                            <EmailIcon sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body1" sx={{ fontSize: '13px', lineHeight: 1.5, color: '#000' }}>
                              {visit.email}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.4 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body1" sx={{ fontSize: '13px', lineHeight: 1.5, color: '#000' }}>
                              {visit.phone}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body1" sx={{ fontSize: '13px', lineHeight: 1.5, color: '#000' }}>
                              {visit.city}, {visit.state}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                          value={getCardTabValue(visit.id + '_col2')}
                          onChange={(e, v) => handleCardTabChange(visit.id + '_col2', v)}
                          variant="fullWidth"
                          sx={{ minHeight: 42, '& .MuiTab-root': { minHeight: 42, py: 1, textTransform: 'none', fontSize: '13px', fontWeight: 600 } }}
                        >
                          <Tab label="Visit Details" />
                          <Tab label="Source" />
                        </Tabs>
                      </Box>
                      <Box sx={{ mt: 1.5, minHeight: 140 }}>
                        {getCardTabValue(visit.id + '_col2') === 0 && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '14px' }}>
                              Visit Details
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Interested In:</strong> {visit.interestedIn}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Budget:</strong> {visit.budgetUpto}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Project:</strong> {Array.isArray(visit.projectsDetails) && visit.projectsDetails.length > 0
                                ? visit.projectsDetails.map(p => p.projectName || p.name || 'Unknown').join(', ')
                                : visit.projectInterested || 'N/A'}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Visited By:</strong> {visit.visitedBy}
                            </Typography>
                          </Box>
                        )}
                        {getCardTabValue(visit.id + '_col2') === 1 && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '14px' }}>
                              Source Details
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Source:</strong> {visit.source}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Lead ID:</strong> {visit.leadId}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Reference:</strong> {visit.referenceName}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Visit Remark:</strong> {visit.visitRemark}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box>
                      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                          value={getCardTabValue(visit.id + '_col3')}
                          onChange={(e, v) => handleCardTabChange(visit.id + '_col3', v)}
                          variant="fullWidth"
                          sx={{ minHeight: 42, '& .MuiTab-root': { minHeight: 42, py: 1, textTransform: 'none', fontSize: '13px', fontWeight: 600 } }}
                        >
                          <Tab label="Timeline" />
                          <Tab label="Associate Details" />
                        </Tabs>
                      </Box>
                      <Box sx={{ mt: 1.5, minHeight: 140 }}>
                        {getCardTabValue(visit.id + '_col3') === 0 && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '14px' }}>
                              Timeline
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Visit Created:</strong> {displayDate(visit.visitCreatedOn)}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Lead Created:</strong> {displayDate(visit.leadCreatedOn)}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Next Follow-up:</strong>{' '}
                              <span style={{ color: visit.nextFollowUp && visit.nextFollowUp !== '-' ? formatNextFollowUp(visit.nextFollowUp).color : 'inherit' }}>
                                {visit.nextFollowUp && visit.nextFollowUp !== '-' ? formatNextFollowUp(visit.nextFollowUp).fullText : '-'}
                              </span>
                            </Typography>
                          </Box>
                        )}
                        {getCardTabValue(visit.id + '_col3') === 1 && (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1, color: '#536DFE', fontSize: '14px' }}>
                              Associate Details
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Associate Name:</strong> {visit.visitedBy}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Associate Number:</strong> {visit.visitedByPhone || '-'}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ mb: 0.6, fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Associate Email:</strong> {visit.visitedByEmail || '-'}
                            </Typography>
                            <Typography variant="body1" display="block" sx={{ fontSize: '13px', lineHeight: 1.6 }}>
                              <strong>Associate ID:</strong> {visit.associateId || '-'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
              <Box
                sx={{
                  px: 2,
                  pb: 1.5,
                  pt: 1.5,
                  display: 'flex',
                  gap: 1,
                  borderTop: '1px solid #f0f0f0',
                  flexWrap: 'wrap',
                }}
              >
                {canWrite && (
                  <>
                    <Tooltip title="Edit Visit">
                      <IconButton color="primary" size="small" onClick={() => handleEdit(visit)} sx={{ border: '1px solid #e0e0e0' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Visit">
                      <IconButton color="error" size="small" onClick={() => handleDelete(visit)} sx={{ border: '1px solid #e0e0e0' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                <Button variant="outlined" size="small" startIcon={<VisitIcon />} onClick={() => handleViewDetails(visit)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px', py: 0.5, flex: 1, minWidth: 100 }}>
                  View Details
                </Button>
                {canWrite && (
                  <>
                    <Button variant="outlined" size="small" startIcon={<FollowUpIcon />} onClick={() => handleFollowUp(visit)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px', py: 0.5, flex: 1, minWidth: 100 }}>
                      Follow-up
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<MoneyIcon />} onClick={() => handleBooking(visit)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px', py: 0.5, flex: 1, minWidth: 100 }}>
                      Booking
                    </Button>
                  </>
                )}
                {/* <Button variant="outlined" size="small" startIcon={<TimelineIcon />} onClick={() => {
                  const timelineDetails = [
                    {
                      id: 1,
                      date: visit.visitCreatedOn ? new Date(visit.visitCreatedOn).toLocaleString('en-GB') : '-',
                      type: 'Visit',
                      remark: visit.visitRemark || visit.remark1,
                      nextFollowUp: visit.nextFollowUp ? formatNextFollowUp(visit.nextFollowUp).fullText : '-',
                      status: visit.status ,
                      performedBy: visit.visitedBy || visit.createdBy ,
                    },
                    {
                      id: 2,
                      date: visit.leadCreatedOn ? new Date(visit.leadCreatedOn).toLocaleString('en-GB') : '-',
                      type: 'Lead',
                      remark: visit.remark1 || visit.lastFollowUpRemark ,
                      nextFollowUp: visit.nextFollowUp ? formatNextFollowUp(visit.nextFollowUp).fullText : '-',
                      status: visit.status ,
                      performedBy: visit.createdBy || visit.visitedBy,
                    },
                  ];

                  // Add follow-up entries if available
                  if (visit.followUpDate) {
                    timelineDetails.unshift({
                      id: timelineDetails.length + 1,
                      date: new Date(visit.followUpDate).toLocaleString('en-GB'),
                      type: 'Follow-up',
                      remark: visit.lastFollowUpRemark ,
                      nextFollowUp: visit.nextFollowUp ? formatNextFollowUp(visit.nextFollowUp).fullText : '-',
                      status: visit.status || 'HOT',
                      performedBy: visit.visitedBy || visit.createdBy ,
                    });
                  }

                  setTimelineSelectedVisit({
                    ...visit,
                    timelineDetails: timelineDetails.reverse()
                  });
                  setTimelineSelectedAssociate(visit.visitedBy || '');
                  setTimelineSelectedVisitId(visit.visitId || '');
                  setTimelineHasSearched(true);
                  setTimelineDialogOpen(true);
                }} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '12px', py: 0.5, flex: 1, minWidth: 100 }}>
                  Timeline
                </Button> */}
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {data.length > 0 && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <TablePagination

            component="div"
            count={totalDocs}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              border: '3px solid white',
              backgroundColor: selectedVisit ? getAvatarColor(selectedVisit.name) : '#536DFE',
              fontSize: '1.5rem',
              fontWeight: 700,
            }}
          >
            {selectedVisit ? getAvatarInitials(selectedVisit.name) : ''}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedVisit?.name}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>{selectedVisit?.visitId} | {selectedVisit?.leadId}</Typography>
          </Box>
          <Chip
            label={selectedVisit?.status}
            size="small"
            sx={{
              backgroundColor: selectedVisit ? getStatusColor(selectedVisit.status, statusColors).backgroundColor : 'white',
              color: selectedVisit ? getStatusColor(selectedVisit.status, statusColors).color : '#536DFE',
              fontWeight: 600,
              fontSize: '12px',
              height: '28px',
              border: selectedVisit ? 'none' : '1px solid #536DFE'
            }}
          />
        </DialogTitle>
        <DialogContent sx={{ px: 2, pt: 2, pb: 2, maxHeight: '70vh', overflow: 'auto' }}>
          {selectedVisit && (
            <Box>
              {/* Personal Information Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1, fontSize: '16px' }}>
                  <PersonIcon sx={{ fontSize: 20 }} />
                  Personal Information
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Full Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.name}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: '#666' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                          Phone Number
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.phone}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                          WhatsApp
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.whatsapp || 'N/A'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: '#666' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                          Email Address
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.email}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOnIcon sx={{ fontSize: 16, color: '#666' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                          Address
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>
                          {selectedVisit.address}, {selectedVisit.city}, {selectedVisit.state}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Visit & Lead Information Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1, fontSize: '16px' }}>
                  <VisitIcon sx={{ fontSize: 20 }} />
                  Visit & Lead Information
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Visit ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, color: '#536DFE', fontSize: '14px' }}>{selectedVisit.visitId}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Lead ID
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, color: '#536DFE', fontSize: '14px' }}>{selectedVisit.leadId}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Visit Created On
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{formatDateFull(selectedVisit.visitCreatedOn)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Lead Created On
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{formatDateFull(selectedVisit.leadCreatedOn)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="datetime-local"
                      label="Next Follow Up Date"
                      value={editFormData.nextFollowUpDate}
                      onChange={(e) => handleEditFormChange('nextFollowUpDate', e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Last Follow Up Date
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>
                        {selectedVisit.followUpDate || '-'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Property Interest Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1, fontSize: '16px' }}>
                  <BusinessIcon sx={{ fontSize: 20 }} />
                  Property Interest & Budget
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Interested In
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.interestedIn}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Budget Upto
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, color: '#2e7d32', fontSize: '14px' }}>{selectedVisit.budgetUpto}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Project Interested
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>
                        {Array.isArray(selectedVisit.projectsDetails) && selectedVisit.projectsDetails.length > 0
                          ? selectedVisit.projectsDetails.map(p => p.projectName || p.name || 'Unknown').join(', ')
                          : selectedVisit.projectInterested || 'N/A'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Source & Reference Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1, fontSize: '16px' }}>
                  <AssignmentIcon sx={{ fontSize: 20 }} />
                  Source & Reference Information
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Source
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.source}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Reference Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.referenceName}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Visit Details & Remarks Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1, fontSize: '16px' }}>
                  <CalendarIcon sx={{ fontSize: 20 }} />
                  Visit Details & Remarks
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Visit Remark
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, lineHeight: 1.5, fontSize: '14px' }}>
                        {selectedVisit.visitRemark}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Follow Up Remark"
                      value={editFormData.followUpRemark}
                      onChange={(e) => handleEditFormChange('followUpRemark', e.target.value)}
                      size="small"
                      placeholder="Enter follow-up remark..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Remark 1
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, lineHeight: 1.5, fontSize: '14px' }}>
                        {selectedVisit.remark1}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Remark 2
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5, lineHeight: 1.5, fontSize: '14px' }}>
                        {selectedVisit.remark2}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Visit Selfies
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>
                        {selectedVisit.visitSelfies || 0} photo(s)
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Created By & Visited By Section */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5, color: '#536DFE', display: 'flex', alignItems: 'center', gap: 1, fontSize: '16px' }}>
                  <PersonIcon sx={{ fontSize: 20 }} />
                  Created & Visited By
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Visited By
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.visitedBy}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', fontWeight: 600 }}>
                        Created By
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5, fontSize: '14px' }}>{selectedVisit.createdBy}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Inquiry Status</InputLabel>
                      <Select
                        value={editFormData.inquiryStatus}
                        onChange={(e) => handleEditFormChange('inquiryStatus', e.target.value)}
                        label="Inquiry Status"
                      >
                        {statusOptions.map(option => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                        {/* Fallback items */}
                        {!statusOptions.includes('HOT') && <MenuItem value="HOT">HOT</MenuItem>}
                        {!statusOptions.includes('WARM') && <MenuItem value="WARM">WARM</MenuItem>}
                        {!statusOptions.includes('COLD') && <MenuItem value="COLD">COLD</MenuItem>}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3, borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}>
          <Button onClick={() => setDetailsDialogOpen(false)} variant="outlined" disabled={savingDetails} sx={{ fontWeight: 600 }}>
            Close
          </Button>
          {canWrite && (
            <>
              <Button onClick={() => selectedVisit && handleEdit(selectedVisit)} variant="outlined" startIcon={<EditIcon />} disabled={savingDetails} sx={{ fontWeight: 600 }}>
                Edit Full Visit
              </Button>
              <Button
                onClick={handleSaveDetails}
                variant="contained"
                startIcon={savingDetails ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                disabled={savingDetails}
                sx={{ backgroundColor: '#536DFE', fontWeight: 600 }}
              >
                {savingDetails ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={followUpDialogOpen} onClose={() => setFollowUpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#536DFE', color: 'white', display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2 }}>
          <FollowUpIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Follow-up</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Customer Name: {selectedVisit?.name} Phone No. {selectedVisit?.phone}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 3, overflow: 'visible' }}>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Follow-up Date"
                  value={followUpData.followUpDate}
                  onChange={(e) => handleFollowUpFormChange('followUpDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Inquiry Status</InputLabel>
                  <Select
                    value={followUpData.inquiryStatus}
                    onChange={(e) => handleFollowUpFormChange('inquiryStatus', e.target.value)}
                    label="Inquiry Status *"
                  >
                    {statusOptions.map(option => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                    {/* Fallback items */}
                    {!statusOptions.includes('HOT') && <MenuItem value="HOT">HOT</MenuItem>}
                    {!statusOptions.includes('WARM') && <MenuItem value="WARM">WARM</MenuItem>}
                    {!statusOptions.includes('COLD') && <MenuItem value="COLD">COLD</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Visit Type</InputLabel>
                  <Select
                    value={followUpData.visitType}
                    onChange={(e) => handleFollowUpFormChange('visitType', e.target.value)}
                    label="Visit Type *"
                  >
                    <MenuItem value="Office Visit">Office Visit</MenuItem>
                    <MenuItem value="Site Visit">Site Visit</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Next Followup Date"
                  value={followUpData.nextFollowUpDate}
                  onChange={(e) => handleFollowUpFormChange('nextFollowUpDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Remark"
                  value={followUpData.remark}
                  onChange={(e) => handleFollowUpFormChange('remark', e.target.value)}
                  placeholder="Enter follow-up notes..."
                  required
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#000' }}>
              Recent Follow Up Detail
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {followUpLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2, maxHeight: 300 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>S/No.</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Followup Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Remark</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Next Schedule date</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '12px' }}>CreatedBy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {followUpHistory.length > 0 ? (
                      followUpHistory.map((f, idx) => (
                        <TableRow key={f.id} hover>
                          <TableCell sx={{ fontSize: '12px' }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>
                            <Chip label={f.type} size="small" color="primary" variant="outlined" sx={{ fontSize: '11px' }} />
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{f.followUpDate}</TableCell>
                          <TableCell sx={{ fontSize: '12px', maxWidth: 150 }}>{f.remark}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{f.nextScheduleDate || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>
                            <Chip
                              label={f.status}
                              size="small"
                              sx={{
                                fontSize: '11px',
                                backgroundColor: getStatusColor(f.status, statusColors).backgroundColor,
                                color: getStatusColor(f.status, statusColors).color,
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{f.createdBy}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary" variant="body2">No follow-up records found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setFollowUpDialogOpen(false)}
              disabled={followUpLoading}
            >
              Cancel
            </Button>
            {canWrite && (
              <Button
                variant="contained"
                onClick={handleFollowUpSave}
                startIcon={followUpLoading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                disabled={followUpLoading}
                sx={{ backgroundColor: '#536DFE' }}
              >
                {followUpLoading ? 'Saving...' : 'Save'}
              </Button>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog
        open={timelineDialogOpen}
        onClose={() => setTimelineDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
            maxWidth: '900px',
            maxHeight: '85vh'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: '8px 8px 0 0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <TimelineIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Site Visit Timeline
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {timelineSelectedVisit?.name ? `${timelineSelectedVisit.visitId} | ${timelineSelectedVisit.leadId}` : 'View timeline details'}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setTimelineDialogOpen(false)}
            sx={{ color: 'white' }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, maxHeight: 'calc(85vh - 140px)', overflow: 'auto' }}>
          {timelineSelectedVisit && (
            <Box sx={{ p: 3 }}>
              {/* Timeline Details Card */}
              <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#536DFE',
                    color: 'white',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <TimelineIcon sx={{ fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Timeline Details
                    </Typography>
                  </Box>
                </Box>

                <CardContent sx={{ p: 0 }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Date & Time</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Remark</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Next Follow Up</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '14px' }}>Performed By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {timelineSelectedVisit.timelineDetails && timelineSelectedVisit.timelineDetails.length > 0 ? (
                          timelineSelectedVisit.timelineDetails.map((timeline, index) => (
                            <TableRow key={timeline.id || index} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                                  {timeline.date}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={timeline.type}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 600, fontSize: '12px' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '13px' }}>
                                  {timeline.remark}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: timeline.nextFollowUp ? '#1976d2' : '#999' }}>
                                  {timeline.nextFollowUp || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={timeline.status}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    ...getStatusColor(timeline.status, statusColors),
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      fontSize: '12px',
                                      bgcolor: getAvatarColor(timeline.performedBy),
                                      fontWeight: 700,
                                    }}
                                  >
                                    {getAvatarInitials(timeline.performedBy)}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '13px' }}>
                                    {timeline.performedBy}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No timeline details available
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setTimelineDialogOpen(false)} variant="contained" sx={{ backgroundColor: '#536DFE' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>



      <AlertModal
        open={alertData.open}
        onClose={() => setAlertData({ ...alertData, open: false })}
        title={alertData.type}
        message={alertData.message}
        type={alertData.type.toLowerCase() === 'success' ? 'success' : alertData.type.toLowerCase() === 'error' ? 'error' : alertData.type.toLowerCase() === 'warning' ? 'warning' : 'info'}
      />

      <ConfirmModal
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        onConfirm={() => {
          if (confirmDialog.action) confirmDialog.action();
          setConfirmDialog({ ...confirmDialog, open: false });
        }}
        type={confirmDialog.type}
      />
    </Box>
  );
};

export default SiteVisit;
